import * as Api from '@ViewModels';
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { GetSurveyByIdSurveyType, useGetSurveyById } from '../../queries';

interface UseClonableSurveyResult<T> {
	surveyData: T | undefined;
	isClone: boolean;
	isEdit: boolean;
	isLoading: boolean;
}

export function useClonableSurvey<T extends Api.ISurvey = Api.ISurvey>(
	surveyId: string,
	impersonationContext?: Api.IImpersonationContext,
	surveyType?: GetSurveyByIdSurveyType
): UseClonableSurveyResult<T> {
	const location = useLocation();
	const searchParams = new URLSearchParams(location.search);
	const cloneId = searchParams.get('clone');
	const isClone = Boolean(cloneId);
	const isEdit = Boolean(surveyId) && !isClone;

	const surveyQuery = useGetSurveyById<T>({
		impersonationContext,
		enabled: Boolean(surveyId),
		surveyId,
		surveyType,
	});

	const cloneSurveyQuery = useGetSurveyById<T>({
		impersonationContext,
		enabled: isClone,
		surveyId: cloneId || '',
		surveyType,
	});

	const surveyData = useMemo(() => {
		return surveyQuery.data || cloneSurveyQuery.data;
	}, [surveyQuery.data, cloneSurveyQuery.data]);

	const isLoading = (isEdit && surveyQuery.isPending) || (isClone && cloneSurveyQuery.isPending);

	return {
		surveyData,
		isClone,
		isEdit,
		isLoading,
	};
}
